import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Pagination,
} from '@mui/material';
import ProjectList from '../components/ProjectList';
import useProjectStore from '../store/useProjectStore';

const PAGE_SIZE = 6;

const FacultyProjectDashboard = () => {
  const projects = useProjectStore((s) => s.projects);
  const loadingProjects = useProjectStore((s) => s.loadingProjects);
  const errorProjects = useProjectStore((s) => s.errorProjects);
  const fetchProjects = useProjectStore((s) => s.fetchProjects);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const searchFiltered = useMemo(() => {
    const raw = Array.isArray(projects) ? projects : [];
    const term = search.trim().toLowerCase();
    if (!term) return raw;
    return raw.filter((p) => {
      const name = (p.student?.name || '').toLowerCase();
      const idStr = String(p.student?.id ?? p.student?._id ?? '').toLowerCase();
      return name.includes(term) || idStr.includes(term);
    });
  }, [projects, search]);

  const sortedProjects = useMemo(() => {
    return [...searchFiltered].sort((a, b) => {
      const idA = a.student?.id ?? '';
      const idB = b.student?.id ?? '';
      return String(idA).localeCompare(String(idB), undefined, { numeric: true });
    });
  }, [searchFiltered]);

  const totalPages = Math.max(1, Math.ceil(sortedProjects.length / PAGE_SIZE) || 1);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const paginatedProjects = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedProjects.slice(start, start + PAGE_SIZE);
  }, [sortedProjects, page]);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5, letterSpacing: '-0.02em' }}>
        Student Projects
      </Typography>

      <Box sx={{ mb: 3 }}>
        <TextField
          label="Search by Student Name or ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: { sm: 280 } }}
        />
      </Box>

      {loadingProjects ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : errorProjects ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load projects. Please try again later.
        </Alert>
      ) : (
        <>
          <ProjectList
            projects={paginatedProjects}
            showStudentDetails
            showEvaluate
            onProjectUpdate={() => fetchProjects()}
            onProjectDelete={() => fetchProjects()}
          />
          {sortedProjects.length > PAGE_SIZE ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          ) : null}
        </>
      )}
    </Box>
  );
};

export default FacultyProjectDashboard;
